import { useEffect } from 'react';
import { Channel as ChannelType, ChannelFilters, ChannelSort } from 'stream-chat';
import { Channel, ChannelList } from 'stream-chat-react';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';
import { useSetAtom } from 'jotai';

import { useChatClient, useUserBanListener } from '@bizzabo/chat-core';
import { Theme } from '@bizzabo/design-system';
import { useCurrentUser } from '@bizzabo/entities';
import { instanaCustomEvent } from '@bizzabo/instana-api';

import CustomChannelsList from './components/CustomChannelsList';
import CustomChannelListItem from './components/CustomChannelListItem';
import ChannelPage from './components/ChannelPage';
import { useMessages, View } from '../contexts/MessagesContext';
import useTypeTracking from '../../../analytics';
import ChatHeader from './components/ChatHeader';
import ContactListWrapper from '../../components/ContactListWrapper';
import ZeroView from './components/ZeroView';
import MessageNotification from './components/MessageNotification';
import HiddenView from './components/HiddenView';
import BanUserView from './components/BanUserView/BanUserView';
import { createChannelAtom } from '..';

const CHANNEL_TYPE = 'private';
const OLD_ENOUGH_DATE_TO_FILTER_OUT_CHANNELS_WITHOUT_MESSAGES = '2020-01-01T00:00:00.00000Z';

const PrivateChat: React.FC = () => {
    const classes = useStyles();
    const { trackSystemEvent } = useTypeTracking();
    const { client } = useChatClient();
    const { eventId, event, view, searchText } = useMessages();
    const { data: currentUser, isLoading } = useCurrentUser(eventId);
    const isMyUserBanned = useUserBanListener(client);
    const createChannelWith = useSetAtom(createChannelAtom);

    const filters: ChannelFilters = {
        type: CHANNEL_TYPE,
        members: { $in: [client?.userID] },
        last_message_at: { $gte: OLD_ENOUGH_DATE_TO_FILTER_OUT_CHANNELS_WITHOUT_MESSAGES },
    };
    const sort: ChannelSort = [{ last_updated: -1 }];

    useEffect(() => {
        trackSystemEvent({
            description: 'LOADED',
        });
        instanaCustomEvent({ eventName: 'messages load' });
    }, []);

    if (!client || isLoading) {
        return null;
    }

    const handleContactItemClick = (attendeeId: number) => {
        if (attendeeId) {
            createChannelWith({ members: [attendeeId], eventId });
        }
    };

    const customChannelFilterFunction = (channels: ChannelType[]) => {
        return channels.filter(channel => channel.type === CHANNEL_TYPE);
    };

    return (
        <div className={classes.chatContainer}>
            <div
                className={cn(classes.channelList, {
                    show: view === View.ChannelList || view === View.Search || currentUser.isHidden,
                })}
            >
                <ChatHeader />
                {currentUser.isHidden && !isMyUserBanned && <HiddenView />}
                {!currentUser.isHidden && !isMyUserBanned && view === View.Search && (
                    <ContactListWrapper
                        eventId={eventId}
                        searchTerm={searchText}
                        onContactItemClick={handleContactItemClick}
                    />
                )}
                {!currentUser.isHidden && !isMyUserBanned && view === View.ChannelList && (
                    <ChannelList
                        channelRenderFilterFn={customChannelFilterFunction}
                        EmptyStateIndicator={ZeroView}
                        List={CustomChannelsList}
                        Preview={CustomChannelListItem}
                        filters={filters}
                        sort={sort}
                        setActiveChannelOnMount={false}
                    />
                )}
                {isMyUserBanned && <BanUserView event={event} />}
            </div>
            {!currentUser.isHidden && !isMyUserBanned && view === View.Channel && (
                <Channel MessageNotification={MessageNotification} TypingIndicator={() => null}>
                    <ChannelPage />
                </Channel>
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        chatContainer: {
            height: '100%',

            '& .str-chat-channel-list': {
                float: 'none',
                backgroundColor: theme.palette.greys[100],
                borderRadius: '0 0 33px 33px',
                overflowX: 'hidden',
            },

            '& .str-chat-channel': {
                height: '100%',
                borderRadius: '33px 33px 0 0',
                overflowX: 'hidden',
                [theme.breakpoints.up('sm')]: {
                    borderRadius: '0 0 33px 33px',
                },
            },
        },

        channelList: {
            height: '100%',
            display: 'none',
            top: 0,
            left: 0,
            transform: 'translate3d(-100vw, 0, 0)',
            transition: 'transform 0.3s cubic-bezier(0, 0.52, 0, 1)',

            '&.show': {
                display: 'flex',
                flexDirection: 'column',
                transform: 'translate3d(0vw, 0, 0)',
            },

            '& .str-chat': {
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% - 48px)',
            },
        },
    }),
    { classNamePrefix: PrivateChat.name }
);

export default PrivateChat;
